import React from "react";
import { Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  CARD_BORDER,
  CLOSE_ALERT_BG,
  CLOSE_ALERT_BORDER,
  CLOSE_ALERT_TEXT,
  DEBT_CARD_TITLE_FONT_SIZE,
  NAVY,
} from "../debt-collection-section/DebtCollectionSectionConstants";

/**
 * Form modal to close a debt account early with date, remark, and read-only closed-by field.
 * @param {object} props - Modal props.
 * @param {boolean} props.show - Whether the modal is visible.
 * @param {boolean} props.closeSubmitting - Disables actions while closing.
 * @param {function} props.onHide - Dismiss handler.
 * @param {function} props.onConfirmClose - Submits closure to the API.
 * @param {{ closure_date: Date, remark: string }} props.closeForm - Controlled closure form state.
 * @param {function} props.setCloseForm - State setter for closure form.
 * @param {string} props.closeRemarkError - Validation message for remark.
 * @param {function} props.setCloseRemarkError - Clears or sets remark error.
 * @param {string} props.closeAccountClosedByField - Read-only display for who closes the account.
 * @returns {JSX.Element} Bootstrap modal with date picker and remark.
 */
export function DebtCollectionCloseAccountModal({
  show,
  closeSubmitting,
  onHide,
  onConfirmClose,
  closeForm,
  setCloseForm,
  closeRemarkError,
  setCloseRemarkError,
  closeAccountClosedByField,
}) {
  return (
    <Modal
      show={show}
      onHide={() => !closeSubmitting && onHide()}
      centered
      container={typeof document !== "undefined" ? document.body : undefined}
      className="debt-close-account-modal-root"
      backdropClassName="debt-close-account-backdrop"
      dialogClassName="rounded-lg shadow debt-close-account-dialog"
    >
      <Modal.Header
        closeButton
        className="border-0 pb-0"
        style={{
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingTop: "20px",
        }}
      >
        <div>
          <Modal.Title
            className="font-weight-bolder mb-2"
            style={{ color: NAVY, fontSize: DEBT_CARD_TITLE_FONT_SIZE }}
          >
            Close account
          </Modal.Title>
          <p className="text-muted small mb-0 pr-4">
            Use when the configuration is being ended before the balance is
            fully collected.
          </p>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: "16px 24px 8px" }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <Form.Group className="mb-0">
              <Form.Label className="text-muted small">
                Closure date *
              </Form.Label>
              <div className="position-relative debt-close-modal-date-field w-100">
                <DatePicker
                  className="form-control debt-close-modal-date-input"
                  wrapperClassName="w-100"
                  selected={closeForm.closure_date}
                  onChange={(d) =>
                    setCloseForm((c) => ({
                      ...c,
                      closure_date: d || new Date(),
                    }))
                  }
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  popperPlacement="bottom"
                  popperClassName="debt-close-datepicker-popper"
                />
                <button
                  type="button"
                  className="debt-close-modal-date-calendar-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    const wrap = e.currentTarget.previousElementSibling;
                    const input =
                      wrap &&
                      wrap.querySelector &&
                      wrap.querySelector("input");
                    if (input) {
                      input.focus();
                      input.click();
                    }
                  }}
                  aria-label="Open calendar"
                >
                  <CalendarTodayIcon
                    fontSize="small"
                    className="text-muted"
                  />
                </button>
              </div>
            </Form.Group>
          </div>
          <div className="col-md-6 mb-3">
            <Form.Group className="mb-0">
              <Form.Label className="text-muted small">Closed by</Form.Label>
              <Form.Control
                readOnly
                disabled
                className="bg-light text-muted"
                value={closeAccountClosedByField}
              />
            </Form.Group>
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label className="text-muted small">Remark *</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={closeForm.remark}
            onChange={(e) => {
              const v = e.target ? e.target.value : "";
              setCloseForm((c) => ({ ...c, remark: v }));
              if (closeRemarkError) setCloseRemarkError("");
            }}
            placeholder="Reason for premature closure"
            isInvalid={!!closeRemarkError}
          />
          <Form.Control.Feedback type="invalid">
            {closeRemarkError}
          </Form.Control.Feedback>
        </Form.Group>
        <div
          className="rounded px-3 py-2 small"
          style={{
            backgroundColor: CLOSE_ALERT_BG,
            border: `1px solid ${CLOSE_ALERT_BORDER}`,
            color: CLOSE_ALERT_TEXT,
          }}
        >
          Closing the account changes status to Closed and preserves the audit
          trail.
        </div>
      </Modal.Body>
      <Modal.Footer
        className="border-0 pt-2"
        style={{
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingBottom: "20px",
        }}
      >
        <Button
          variant="light"
          className="border"
          style={{ borderColor: CARD_BORDER }}
          onClick={onHide}
          disabled={closeSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="dark"
          className="rounded"
          style={{ backgroundColor: NAVY, borderColor: NAVY }}
          onClick={onConfirmClose}
          disabled={closeSubmitting}
        >
          {closeSubmitting ? "Closing…" : "Confirm close"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
